import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gauge, Info } from 'lucide-react';
import { AiQuotaCompanyUsageView, getMyCompanyAiQuota } from '../../../api/AI/settings';
import { PageLayout } from '../../../components/ui/PageLayout';
import { Progress } from '../../../components/ui/Progress';
import { Spinner } from '../../../components/ui/Spinner';
import { formatUtcReset, hoursUntilReset, isQuotaLow } from '../../../utils/aiQuota';

/**
 * CO-355 — the HR / Admin view of the company's daily AI allowance. READ ONLY, by owner decision:
 * the quota is a commercial lever owned by the Mowazaf operator.
 *
 * 🔴 There is deliberately NO edit affordance anywhere on this page — not a disabled input, not a
 * greyed-out save button. A disabled control is a promise that something is editable elsewhere in
 * the same screen; the honest design is to state plainly who sets the number and how to ask for a
 * different one. That single sentence is what turns a bare refusal into something a person can act
 * on, which is the whole reason the screen exists.
 *
 * (The read-only endpoint behind it exposes no mutating verb, so the absence of a control here is
 * not what keeps HR out — see AiQuotaController.)
 */

const TIER_KEYS: Record<string, string> = {
  hrAdmin: 'aiQuota.tier.hrAdmin',
  chiefManager: 'aiQuota.tier.chiefManager',
  employee: 'aiQuota.tier.employee',
};

const AiQuotaOverview: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<AiQuotaCompanyUsageView | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getMyCompanyAiQuota();
        if (cancelled) return;
        if (result.status === 200 && result.payload) setData(result.payload);
        else setFailed(true);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const resetLine = data
    ? t('aiQuota.hr.resets', {
      resetsAt: formatUtcReset(data.resetsAtUtc, i18n.language),
      hours: hoursUntilReset(data.resetsAtUtc),
    })
    : '';

  const tiers = data
    ? [
      { key: 'hrAdmin', limit: data.hrAdminLimit },
      { key: 'chiefManager', limit: data.chiefManagerLimit },
      { key: 'employee', limit: data.employeeLimit },
    ]
    : [];

  return (
    <PageLayout
      title={t('aiQuota.hr.title')}
      description={t('aiQuota.hr.description')}
      icon={Gauge}
      accentColor="slate"
      maxWidth="5xl"
    >
      {loading ? (
        <div className="flex justify-center p-10"><Spinner size="lg" /></div>
      ) : failed || !data ? (
        <p role="alert" className="text-sm text-destructive">{t('aiQuota.hr.loadError')}</p>
      ) : !data.metered ? (
        <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-5 max-w-[70ch]">
          <Info size={18} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm leading-relaxed text-foreground">
            {data.exemptReason === 'ownKey'
              ? t('aiQuota.hr.notMeteredOwnKey')
              : t('aiQuota.hr.notMeteredDisabled')}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-10">
          {/* Your own allowance — the number that actually binds the person reading the page. */}
          <section aria-labelledby="ai-quota-mine" className="flex flex-col gap-3 max-w-lg">
            <div className="flex items-baseline justify-between gap-4">
              <h2 id="ai-quota-mine" className="text-sm font-medium text-foreground">
                {t('aiQuota.hr.yourAllowance')}
              </h2>
              <span className="text-xs text-muted-foreground">
                {t(TIER_KEYS[data.myTier] ?? TIER_KEYS.employee)}
              </span>
            </div>
            <Progress
              value={data.myUsedToday}
              max={Math.max(1, data.myLimit)}
              size="sm"
              variant={isQuotaLow(data.myUsedToday, data.myLimit) ? 'warning' : 'default'}
            />
            <p className="text-sm tabular-nums text-foreground">
              {t('aiQuota.hr.remaining', {
                remaining: Math.max(0, data.myLimit - data.myUsedToday),
                limit: data.myLimit,
              })}
            </p>
          </section>

          {/* Every tier's cap, so HR can answer "why was my employee cut off and I wasn't". */}
          <section aria-labelledby="ai-quota-tiers" className="flex flex-col gap-3">
            <h2 id="ai-quota-tiers" className="text-sm font-medium text-foreground">
              {t('aiQuota.hr.limitsTitle')}
            </h2>
            <dl className="max-w-md divide-y divide-border border-y border-border">
              {tiers.map(tier => (
                <div key={tier.key} className="flex items-center justify-between gap-6 py-2.5">
                  <dt className="text-sm text-muted-foreground">{t(TIER_KEYS[tier.key])}</dt>
                  <dd className="text-sm font-medium tabular-nums text-foreground">
                    {t('aiQuota.hr.perDay', { limit: tier.limit })}
                  </dd>
                </div>
              ))}
            </dl>
          </section>

          <section aria-labelledby="ai-quota-company" className="flex flex-col gap-2">
            <h2 id="ai-quota-company" className="text-sm font-medium text-foreground">
              {t('aiQuota.hr.companyToday')}
            </h2>
            <p className="text-sm tabular-nums text-foreground">
              {data.companyUsedToday > 0
                ? t('aiQuota.hr.companyTodayValue', { requests: data.companyUsedToday, users: data.activeUsersToday })
                : t('aiQuota.hr.companyTodayNone')}
            </p>
            <p className="text-xs text-muted-foreground">{resetLine}</p>
          </section>

          <p className="max-w-[70ch] border-t border-border pt-5 text-xs leading-relaxed text-muted-foreground">
            {t('aiQuota.hr.setByOperator')}
          </p>
        </div>
      )}
    </PageLayout>
  );
};

export default AiQuotaOverview;
